import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom-v5-compat";
import { trackEvent } from "@hopper-b2b/api";
import {
  useExperiment,
  useGetExperimentPayload,
  useGetExperimentVariant,
} from "@hopper-b2b/experiments";
import { I18nMarkup, Trans, useI18nContext } from "@hopper-b2b/i18n";
import { AIR_ENTRY, WalletTrackingEvents } from "@hopper-b2b/types";
import { ButtonWrap, Divider } from "@hopper-b2b/ui";
import type { FeatureFlagsContextProps } from "@hopper-b2b/utilities";
import clsx from "clsx";

import { getOffers } from "../../api/wallet/fetchOffers";
import { EXPERIMENTS, FEATURE_FLAGS, UserContext } from "../../App";
import AirplaneIconHome from "../../assets/client/airplane-icon-home.svg";
import ArrowBack from "../../assets/client/arrow-back.svg?react";
import BellOutlineIcon from "../../assets/client/bell-outline.svg";
import BlackFridayWhite from "../../assets/client/black-friday-white.svg?react";
import Chevron from "../../assets/client/chevron.svg";
import Coin from "../../assets/client/coin.svg";
import BellOutlineIconWhite from "../../assets/client/darkMode/bell-outline-white.svg";
import ChevronWhite from "../../assets/client/darkMode/chevron-white.svg";
import HelpIconWhite from "../../assets/client/darkMode/help-icon-white.svg";
import TripWhiteIcon from "../../assets/client/darkMode/trip-icon-white.svg";

import HelpIcon from "../../assets/client/help-icon.svg";
import HotelIconHome from "../../assets/client/hotel-icon-home.svg";
import TripBlackIcon from "../../assets/client/trip-icon.svg";
import InsuranceIcon from "../../assets/client/uv/insurance.svg";
import NubankUltravioletaLightLogo from "../../assets/client/uv/ultravioleta-logo-purple.svg?react";
import NubankUltravioletaDarkLogo from "../../assets/client/uv/ultravioleta-logo.svg?react";
import CalendarIcon from "../../assets/client/uv/uv-calendar.svg";
import BestPriceGuaranteeIcon from "../../assets/client/uv/uv-dollar.svg";
import PointsInfiniteIcon from "../../assets/client/uv/uv-points-infinity.svg";
import "../../nubank.index.scss";
import { useEnhancedEarnAmount } from "../../hooks/shared";
import {
  isUserTypeCookieUltravioleta,
  useDarkModePreferred,
} from "../../utils/colors";
import { closeNubankWebview, openNubankHelp } from "../../utils/nubankHandoff";
import {
  PATH_FLIGHTS_SEARCH,
  PATH_HOME,
  PATH_HOTELS_SEARCH,
  PATH_OFFERS,
  PATH_TRIPS,
  PATH_WATCHES,
} from "../../utils/urlPaths";
import { FintechBanner } from "../FintechBanner";
import OfferList from "../OfferList";
import OfferTakeover from "../OfferTakeover";
import { ContactSupportAction } from "../Slots/ContactSupportAction";
import { TermsAndConditionsContext } from "../TermsAndConditionsWrapper/component";
import style from "./HomeRouteComponent.module.scss";
import { MaintenanceFlagVariants } from "./index";

interface INubankRoute {
  featureFlags: FeatureFlagsContextProps;
}

const HomeRouteComponent = ({ featureFlags }: INubankRoute) => {
  const { t } = useI18nContext();
  const navigate = useNavigate();
  const { setOpenModal } = useContext(TermsAndConditionsContext);
  const { sessionInfo } = useContext(UserContext);

  // TODO: Update type
  const [offers, setOffers] = useState<any[]>();
  const [offersLoading, setOffersLoading] = useState<boolean>(true);
  const [openOffersModal, setOpenOffersModal] = useState<boolean>(false);
  const featuredOfferIds = useGetExperimentPayload(
    FEATURE_FLAGS.FEATURED_OFFERS
  )?.featuredOfferIds;

  useEffect(() => {
    getOffers()
      .then((res) => {
        const { offers: offersArray } = res;
        let sorted = [...offersArray];
        if (featuredOfferIds?.length > 0) {
          sorted.sort((a, b) => {
            const aIsFeatured = featuredOfferIds?.includes(a.name);
            const bIsFeatured = featuredOfferIds?.includes(b.name);
            if (aIsFeatured === bIsFeatured) return 0;
            return aIsFeatured ? -1 : 1;
          });
        }
        setOffers(sorted);
      })
      .catch((e) => setOffersLoading(false));
  }, [featuredOfferIds]);

  // This is needed to make sure the loading UI doesn't go away till after the offer is loaded in state
  useEffect(() => {
    if (offers !== undefined) {
      setOffersLoading(false);
    }
  }, [offers]);

  const appExcludingTripsMaintenance = useExperiment(
    FEATURE_FLAGS.MAINTENANCE,
    MaintenanceFlagVariants["exclude-trips"]
  );
  const flightsMaintenance = useExperiment(
    FEATURE_FLAGS.MAINTENANCE,
    MaintenanceFlagVariants.flights
  );
  const hotelsMaintenance = useExperiment(
    FEATURE_FLAGS.MAINTENANCE,
    MaintenanceFlagVariants.hotels
  );
  const tripsMaintenance = useExperiment(
    FEATURE_FLAGS.MAINTENANCE,
    MaintenanceFlagVariants.trips
  );

  // Check portal access restrictions
  const portalAccess = sessionInfo?.userInfoResponse?.portalAccess;
  const isFlightsEnabled =
    !appExcludingTripsMaintenance &&
    !flightsMaintenance &&
    portalAccess?.flights !== false;
  const isHotelsEnabled =
    !appExcludingTripsMaintenance &&
    !hotelsMaintenance &&
    portalAccess?.hotels !== false;
  const isTripsEnabled = !tripsMaintenance && portalAccess?.myTrips !== false;

  const isUltravioleta = isUserTypeCookieUltravioleta();
  const darkModeEnabled = useDarkModePreferred();
  const airPriceWatchEnabled = useExperiment(FEATURE_FLAGS.AIR_PRICE_WATCH);
  const maxInstallments = useGetExperimentVariant(EXPERIMENTS.MAX_INSTALLMENTS);
  const blackFridayEnabled = useExperiment(EXPERIMENTS.NUBANK_BLACK_FRIDAY);
  const installmentsCampaignEnabled = useExperiment(
    EXPERIMENTS.INSTALLMENTS_CAMPAIGN
  );
  const blackFridayEnabledVariant = useGetExperimentVariant(
    EXPERIMENTS.NUBANK_BLACK_FRIDAY
  );
  const travelInsuranceEnabled = useExperiment(
    EXPERIMENTS.NUBANK_TRAVEL_INSURANCE
  );
  const enhancedEarnEnabled = useExperiment(
    FEATURE_FLAGS.ENHANCED_EARN_ENABLED
  );
  const { cashbackPercent, pointsPerDollar } = useEnhancedEarnAmount();

  const TripIcon = darkModeEnabled ? TripWhiteIcon : TripBlackIcon;
  const BellIcon = darkModeEnabled ? BellOutlineIconWhite : BellOutlineIcon;
  const NubankUltravioletaLogo = darkModeEnabled
    ? NubankUltravioletaLightLogo
    : NubankUltravioletaDarkLogo;

  const goBack = useCallback(() => {
    closeNubankWebview();
  }, []);

  const goToFaqs = useCallback(() => {
    openNubankHelp();
  }, []);

  const navigateToLodging = useCallback(() => {
    navigate(PATH_HOTELS_SEARCH);
  }, [navigate]);

  const navigateToFlights = useCallback(() => {
    trackEvent({
      eventName: AIR_ENTRY,
      properties: {},
    });
    navigate(PATH_FLIGHTS_SEARCH);
  }, [navigate]);

  const navigateToTrips = useCallback(() => {
    navigate(PATH_TRIPS);
  }, [navigate]);

  const navigateToWatches = useCallback(() => {
    navigate(PATH_WATCHES);
  }, [navigate]);

  const dealsToShow = useMemo(
    () =>
      [
        enhancedEarnEnabled
          ? {
              title: t("homePointsTitle", {
                pointsPerDollar,
              }),
              subtitle: t("homePointsSubtitle", {
                pointsPerDollar,
                cashbackPercent,
              }),
              icon: PointsInfiniteIcon,
            }
          : null,
        {
          title: t("homeBestPriceGuaranteeTitle"),
          subtitle: t("homeBestPriceGuaranteeSubtitle"),
          icon: BestPriceGuaranteeIcon,
        },
        featureFlags.enablePriceDropProtection &&
        featureFlags.enablePricePrediction
          ? {
              title: t("homePriceDropProtectionTitle"),
              subtitle: t("homePriceDropProtectionSubtitle"),
              icon: CalendarIcon,
            }
          : null,
        travelInsuranceEnabled
          ? {
              title: t("homeInsuranceTitle"),
              subtitle: t("homeInsuranceSubtitle"),
              icon: InsuranceIcon,
            }
          : null,
      ].filter((deal) => !!deal),
    [
      enhancedEarnEnabled,
      featureFlags.enablePriceDropProtection,
      featureFlags.enablePricePrediction,
      t,
      travelInsuranceEnabled,
      cashbackPercent,
      pointsPerDollar,
    ]
  );

  const bestDmoOffer = useMemo(() => {
    if (!offers?.length) return null;
    const offerWithDiscount = offers.find(
      (o) => o.discount?.Discount === "Percentage" && o.discount?.percentage > 0
    );
    return offerWithDiscount ?? null;
  }, [offers]);

  const handleOpenOffersModal = useCallback(() => {
    setOpenOffersModal(true);
    // track the open event
    trackEvent({
      eventName: WalletTrackingEvents.tapped_wallet_banner,
      properties: {},
    });
  }, [setOpenOffersModal]);

  const handleCloseOffersModal = useCallback(() => {
    setOpenOffersModal(false);
    navigate(PATH_HOME);
  }, [setOpenOffersModal, navigate]);

  const navigateToOffers = useCallback(() => {
    handleOpenOffersModal();
    navigate(PATH_OFFERS);
  }, [handleOpenOffersModal, navigate]);

  return (
    <div className={style.nubankHome}>
      <div className={clsx("home-top", { "dark-mode": darkModeEnabled })}>
        <div className="home-nav">
          <ButtonWrap onClick={goBack} className="go-back">
            <ArrowBack className="" />
          </ButtonWrap>
          <ButtonWrap onClick={goToFaqs}>
            <img src={darkModeEnabled ? HelpIconWhite : HelpIcon} alt="" />
          </ButtonWrap>
        </div>
        <div className="home-header">
          {isUltravioleta ? (
            <NubankUltravioletaLogo className="uv-logo" />
          ) : null}

          <h2 className="branding" data-cy="branding-title">
            {t("nuTravel")}
          </h2>
          <h1 className="headline">{t("homeScreenTitle")}</h1>
        </div>
        <div className="home-actions">
          {featureFlags.enableAirShopV4 && isFlightsEnabled ? (
            <ButtonWrap
              data-cy="flights-entrypoint"
              className="action-item"
              onClick={navigateToFlights}
            >
              <img src={AirplaneIconHome} alt="" />
              {installmentsCampaignEnabled && maxInstallments ? (
                <span className="home-offer-badge">
                  {t("installmentsMultiple", { installments: maxInstallments })}
                </span>
              ) : null}
              <p>{t("flightSearchPrompt")}</p>
            </ButtonWrap>
          ) : null}
          {isHotelsEnabled ? (
            <ButtonWrap
              data-cy="hotels-entrypoint"
              className="action-item"
              onClick={navigateToLodging}
            >
              <img src={HotelIconHome} alt="" />
              {installmentsCampaignEnabled && maxInstallments ? (
                <span className="home-offer-badge">
                  {t("installmentsMultiple", { installments: maxInstallments })}
                </span>
              ) : bestDmoOffer ? (
                <span className="home-offer-badge">
                  {t("homeOffers.lodging-badge", {
                    percentage: Math.round(
                      bestDmoOffer.discount.percentage * 100
                    ),
                  })}
                </span>
              ) : null}
              <p>{t("searchHotels")}</p>
            </ButtonWrap>
          ) : null}
        </div>
        {installmentsCampaignEnabled ? (
          <FintechBanner
            className="installments-campaign-banner"
            title={t("installmentsCampaign", { installments: maxInstallments })}
            fintechIcon={Coin}
            hideBrand
          />
        ) : bestDmoOffer ? (
          <FintechBanner
            className="installments-campaign-banner"
            title={t("homeOffers.callout", {
              percentage: Math.round(bestDmoOffer.discount.percentage * 100),
            })}
            fintechIcon={Coin}
            hideBrand
            onClick={navigateToOffers}
          />
        ) : null}
      </div>
      <div className="home-bottom">
        {isTripsEnabled ? (
          <ButtonWrap onClick={navigateToTrips}>
            <div className="button-entry-with-text">
              <div className="content-left">
                <img src={TripIcon} alt="" />
                <div className="content">
                  <h3>{t("myTrips")}</h3>
                  <p>{t("homeScreenMyTripsSubtitle")}</p>
                </div>
              </div>
              <img src={darkModeEnabled ? ChevronWhite : Chevron} alt="" />
            </div>
          </ButtonWrap>
        ) : null}
        {airPriceWatchEnabled && isFlightsEnabled ? (
          <ButtonWrap onClick={navigateToWatches}>
            <div className="button-entry-with-text">
              <div className="content-left">
                <img src={BellIcon} alt="" />
                <div className="content">
                  <h3>{t("priceWatch.title")}</h3>
                  <p>{t("monitorAFlight")}</p>
                </div>
              </div>
              <img src={darkModeEnabled ? ChevronWhite : Chevron} alt="" />
            </div>
          </ButtonWrap>
        ) : null}
        {offers?.length > 0 || offersLoading ? (
          <>
            <OfferList offers={offers?.slice(0, 2)} loading={offersLoading} />
            {handleOpenOffersModal ? (
              <button
                className={clsx(style.offerListButton, {
                  loading: offersLoading,
                })}
                onClick={navigateToOffers}
              >
                {t("seeOfferDetails")}
              </button>
            ) : null}
          </>
        ) : null}
        {isUltravioleta ? (
          <>
            <h2 className="find-best-deals">{t("findBestDeals")}</h2>
            {dealsToShow.map((deal, i) => (
              <Fragment key={`deal-to-show-${i}`}>
                <div className="deal-row">
                  <img src={deal?.icon} alt="" className="value-props-icon" />
                  <div>
                    <p className="title">{deal?.title}</p>
                    <p className="subtitle">{deal?.subtitle}</p>
                  </div>
                </div>
                {i < dealsToShow.length - 1 ? <Divider /> : null}
              </Fragment>
            ))}
          </>
        ) : null}
        <Divider />
        <div className="home-contact-support-container">
          <div className="home-tc-link">
            <Trans
              i18nKey={"homeTermsAndConditionsEntry"}
              components={[
                <ButtonWrap
                  className="terms-link"
                  onClick={() => setOpenModal(true)}
                />,
              ]}
            />
          </div>
          <ContactSupportAction />
        </div>
      </div>

      {offers?.length > 0 ? (
        <OfferTakeover
          open={openOffersModal}
          onClose={handleCloseOffersModal}
          offers={offers}
        />
      ) : null}
    </div>
  );
};

export default HomeRouteComponent;
